import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { streamChat } from "../lib/api";

let _sid = 0;
function newSession(prefix) {
  _sid += 1;
  return `${prefix}-${Date.now()}-${_sid}`;
}

export function ChatPanel({ mode = "home", greeting, sessionPrefix = "chat", placeholder = "Type your message...", onMessages, heightClass = "h-[420px]" }) {
  const [sessionId] = useState(() => newSession(sessionPrefix));
  const [messages, setMessages] = useState(
    greeting ? [{ role: "assistant", content: greeting }] : []
  );
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Refresh the greeting bubble when the (async-loaded) greeting changes,
  // as long as the visitor hasn't started chatting yet.
  useEffect(() => {
    setMessages((m) => {
      const hasUser = m.some((x) => x.role === "user");
      if (greeting && !hasUser) return [{ role: "assistant", content: greeting }];
      return m;
    });
  }, [greeting]);

  useEffect(() => {
    if (onMessages) onMessages(messages, sessionId);
  }, [messages, sessionId, onMessages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamChat(
        { session_id: sessionId, message: text, mode },
        (delta) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
            return copy;
          });
        },
        () => setBusy(false)
      );
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col border border-zinc-200 bg-white">
      <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-3">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
        </span>
        <span className="font-mono-label text-[11px] text-zinc-500">AI Assistant · Online</span>
      </div>

      <div ref={scrollRef} className={`flex-1 space-y-4 overflow-y-auto p-4 ${heightClass}`} data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-klein text-white"
                  : "border border-zinc-200 bg-zinc-50 text-zinc-800"
              }`}
            >
              {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 p-3">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-klein"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={busy || !input.trim()}
          className="flex h-10 w-10 items-center justify-center bg-klein text-white transition-colors hover:bg-kleindark disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
